import { eventNames } from "@/modules/Constants";
import * as orientationManager from "@/modules/orientationManager";
import { resize } from "@/stores/experience/actions/layout";
import {
  type FullscreenDocument,
  type FullscreenElement,
  REGION,
  type VideoJsSelf,
} from "@/components/player/videoJsTypes";

// Pseudo-fullscreen: the immersive layout on touch devices.
export function createImmersiveFullscreen(self: VideoJsSelf) {
  // Toggle fullscreen on touch devices. Prefer the REAL element Fullscreen API
  // (Android, desktop) — it removes the browser chrome entirely for true
  // edge-to-edge. iPhone Safari has no element Fullscreen API (confirmed iOS 18.x),
  // so there it falls back to CSS pseudo-fullscreen (browser chrome stays). Either
  // way we then apply our immersive layout so the UI stays ours, never Apple's.
  function toggleImmersiveFullscreen(container: FullscreenElement) {
    const goingOn = !window.__pixPseudoFs;
    const reqFs =
      container.requestFullscreen ||
      container.webkitRequestFullscreen ||
      container.webkitRequestFullScreen;
    const doc = document as FullscreenDocument;
    // iPhone Safari exposes NO element Fullscreen API (confirmed on iOS 18.7:
    // requestFullscreen is undefined), so canRealFs is false there and we fall
    // straight to the CSS fill + force-landscape rotation. On platforms that DO
    // support it (Android/desktop, incl. webkit-only browsers) this gives true
    // edge-to-edge. Check both flags — a webkit-only browser exposes
    // webkitFullscreenEnabled, not the unprefixed one.
    const canRealFs = !!(
      (document.fullscreenEnabled || doc.webkitFullscreenEnabled) &&
      reqFs
    );
    if (canRealFs) {
      try {
        if (goingOn) {
          const p = reqFs.call(container, { navigationUI: "hide" });
          // If real fullscreen is rejected (permission denied / gesture context
          // lost), reconcile: fall back to the full CSS pseudo-fullscreen incl. the
          // force-landscape rotate, so we aren't left in the immersive layout while
          // NOT actually fullscreen.
          if (p && p.catch) {
            p.catch(() => {
              reconcileFailedRealFs();
            });
          }
        } else {
          const exit = document.exitFullscreen
            ? document.exitFullscreen()
            : doc.webkitExitFullscreen
              ? doc.webkitExitFullscreen()
              : null;
          // exit can also reject (e.g. not in fullscreen) — swallow like the request.
          if (exit && exit.catch) {
            exit.catch(() => {
              /* ignore the rejection */
            });
          }
        }
      } catch {
        if (goingOn) {
          reconcileFailedRealFs();
        }
      }
    }
    // Pass whether real fullscreen was used — the CSS force-landscape rotate is only
    // for the iOS pseudo-fullscreen (CSS-fill) path. On Android real fullscreen +
    // screen.orientation.lock rotates the device, so the rotate must NOT apply there
    // (it would briefly flash the CSS-rotated frame before the device turns).
    setImmersiveFullscreen(goingOn, canRealFs);
  }

  // Real fullscreen was requested (canRealFs) but rejected. The immersive layout
  // was already applied assuming real fullscreen (no rotate); fall back to the
  // full CSS pseudo-fullscreen fill by enabling the force-landscape rotate. Guarded
  // on the still-active flag so a late rejection after the viewer already exited
  // no-ops.
  function reconcileFailedRealFs() {
    if (window.__pixPseudoFs && document.body) {
      document.body.classList.add("pix-fs-rotate");
    }
  }

  // Apply (or undo) the immersive-fullscreen LAYOUT: force the landscape immersive
  // UI, lock page scroll, and manage the shop-window sheet. Independent of HOW the
  // viewport is filled (real Fullscreen API vs CSS), so both paths share it.
  function setImmersiveFullscreen(on: boolean, usedRealFs?: boolean) {
    if (!!window.__pixPseudoFs === !!on) {
      return; // already in the requested state
    }
    window.__pixPseudoFs = on;
    if (document.body) {
      document.body.classList.toggle("pix-pseudo-fs-lock", on);
      // The CSS force-landscape rotate is iOS-only (no real fullscreen / no
      // orientation lock). Enable it only on the CSS-fill path so Android real
      // fullscreen (which rotates the device itself) doesn't flash a rotated frame.
      document.body.classList.toggle("pix-fs-rotate", on && !usedRealFs);
    }
    // Force the app's orientation to reflect the pseudo-fullscreen state FIRST, so
    // isLandscape() is true before the shop-window events below — otherwise the
    // open/close of tabs (gated on isLandscape) silently no-ops in portrait.
    try {
      orientationManager.refresh();
    } catch {
      /* ignore */
    }
    // In portrait the shop window is shown by default, so the immersive layout's
    // sliding sheet would open on top of the video. On enter: remember whether it
    // was shown, then close it (the viewer opens tabs via LandscapeNav). On exit:
    // restore it ONLY if it was open before — otherwise exiting in landscape (where
    // nothing was open) would pop a tab.
    // NOTE: entering/exiting pseudo-fs remounts this player (portrait ↔ immersive
    // are different subtrees), so the "was shown" flag lives on window, not on the
    // component instance — the exit runs on a fresh instance.
    if (self.props.onApplicationEvent) {
      const appData = self.props.experienceState.applicationData;
      if (on) {
        window.__pixShopShownBeforeFs = appData.showShopWindow();
        if (window.__pixShopShownBeforeFs) {
          self.props.onApplicationEvent({
            name: eventNames.CLOSE_SHOP_WINDOW,
            region: REGION,
          });
        }
      } else if (window.__pixShopShownBeforeFs) {
        self.props.onApplicationEvent({
          name: eventNames.OPEN_SHOP_WINDOW,
          region: REGION,
        });
      }
    }
    // Flip the toggle icon.
    try {
      if (self.player && !self.player.isDisposed()) {
        self.player.isFullscreen(on);
      }
    } catch {
      /* ignore */
    }
    // Re-render the app into (or out of) the immersive-landscape layout. The
    // resize action always notifies (its handler returns true), so
    // Experience re-reads window.__pixPseudoFs for `useImmersive` and mounts the
    // immersive UI (LandscapeNav tabs + sheet), which already fills the viewport.
    // Call the action directly — a synthetic window 'resize' is deduped by
    // sizeManager because the real viewport size didn't change.
    try {
      resize();
    } catch {
      /* ignore */
    }
  }

  return { toggleImmersiveFullscreen, setImmersiveFullscreen };
}
